import React, { Component } from "react";
import { Table, Grid, Icon } from "semantic-ui-react";
import find from "lodash/find";
import filter from "lodash/filter";

import CardShell from "./CardShell";
import {
  subjectFinalGrade,
  mapehComposite,
  weightedComposite,
  conductFinalGrade,
  generalAverage,
  termAverage,
  passFailLabel,
} from "../cardMath";
import {
  resolveSubjectTemplate,
  isSuppressedRow,
  isMapehComponent,
  MAPEH_COMPONENT_CATEGORIES,
  flattenTemplate,
  pairMapehRows,
  categoryMatches,
  categoryInList,
} from "../subjectTemplate";
import { clubRows, clubMark } from "../clubMembership";


const _ = { find, filter };

const MAPEH_INDENT = { paddingLeft: "30px" };

// "94 (A)" is one mark, so the cell is never allowed to break it in two.
const NO_WRAP = { whiteSpace: "nowrap" };

/**
 * The three-term numeric report card, for Grades 2-10 from SY 2026-2027.
 *
 * Neither senior high year comes here: Grade 11 has its own units-and-electives
 * form in CardSHS11, and Grade 12 its whole-year table in CardSHS12.
 *
 * Differences from the four-quarter card beyond the column count:
 *
 *   - A Descriptor column, carrying DO 015's qualitative label beside the
 *     number (90-100 Advancing, and so on).
 *   - It renders progressively. The old card showed nothing at all until the
 *     fourth quarter was graded, which would mean a blank Term 1 card in
 *     September. Terms 2 and 3 simply print empty until they exist.
 *   - MAPEH components are recognised by one shared list, so "Music and Arts"
 *     and "PEH" are no longer double-counted the way they were for Grades 4,
 *     5, 7 and 8.
 */
class CardNumeric3Term extends Component {
  state = { showAverage: false };

  componentDidMount() {
    this.setState({ showAverage: this.props.sectionStudent.showFinalGradeAverage });
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(prevProps.sectionStudent) !==
      JSON.stringify(this.props.sectionStudent)
    ) {
      this.setState({
        showAverage: this.props.sectionStudent.showFinalGradeAverage,
      });
    }
  }

  toggleAverage = () =>
    this.setState((prev) => ({ showAverage: !prev.showAverage }));

  canSeeAverage() {
    const { userType } = this.props;
    return (
      userType === "admin" || userType === "principal" || userType === "registrar"
    );
  }

  gradeFor(sectionSubject) {
    if (!sectionSubject) return null;
    return _.find(
      this.props.summary.grades,
      (g) => g.sectionSubjectId === sectionSubject.sectionSubjectId
    );
  }

  render() {
    const {
      scheme,
      sectionStudent,
      sectionSubjects,
      teachers,
      remarks,
    } = this.props;

    const section = sectionStudent.section;
    const periods = scheme.periods;

    const template = resolveSubjectTemplate(section, scheme);
    // Grade 6 and JHS teach Music, Arts, PE and Health separately; the form
    // prints two sub-rows under MAPEH, so they are averaged into pairs.
    const subjectCategories = pairMapehRows(flattenTemplate(template));

    const mapehSubjects = _.filter(sectionSubjects, (o) =>
      categoryInList(MAPEH_COMPONENT_CATEGORIES, o.subject.subjectCategory)
    );

    const finalGrades = [];
    let expectedCount = 0;

    // The same subjects the final average counts, term by term, so a term's
    // average is over the whole curriculum rather than whatever is graded.
    const periodMarks = periods.map(() => []);
    const countTerms = (values) =>
      values.forEach((v, i) => { if (Number(v) > 0) periodMarks[i].push(Number(v)); });

    const rows = subjectCategories.map((subjectCategory, key) => {
      const value = subjectCategory.value;

      if (isSuppressedRow(section.gradeLevel, value)) return null;

      // MAPEH has no Subject record of its own; it is the average of whichever
      // of its components the section actually teaches.
      if (value === "MAPEH" && !_.find(sectionSubjects, (o) => o.subject.subjectCategory === "MAPEH")) {
        const componentGrades = mapehSubjects
          .map((s) => this.gradeFor(s))
          .filter(Boolean);
        const composite = mapehComposite(componentGrades, periods);

        if (composite.finalGrade) finalGrades.push(composite.finalGrade);
        countTerms(composite.periodGrades);
        expectedCount++;

        return this.renderRow({
          key,
          label: subjectCategory.text,
          periodValues: composite.periodGrades,
          finalGrade: composite.finalGrade,
        });
      }

      // EPP/TLE is one learning area made of two unequal components: ICT a
      // quarter, AFA/FCS/IA three quarters. The parent carries the weighted
      // mark and counts once; the two print beneath it and count for nothing
      // of their own, exactly as MAPEH's components do.
      if (subjectCategory.components) {
        const parts = subjectCategory.components.map((component) => ({
          component,
          sectionSubject: _.find(sectionSubjects, (o) =>
            categoryMatches(component.value, o.subject.subjectCategory)),
        }));

        const composite = weightedComposite(
          parts.map((part) => ({
            grade: this.gradeFor(part.sectionSubject),
            weight: part.component.weight,
          })),
          periods
        );

        // Nothing graded in either component yet: fall back to the subject the
        // section already carries, so marks entered before the split appear.
        const own = this.gradeFor(_.find(sectionSubjects, (o) =>
          categoryMatches(value, o.subject.subjectCategory)));
        const useOwn = composite.finalGrade === 0 &&
          composite.periodGrades.every((v) => !v) && own;

        const periodValues = useOwn
          ? periods.map((p) => Number(own[p.dbField]) || 0)
          : composite.periodGrades;
        const finalGrade = useOwn
          ? subjectFinalGrade(own, periods)
          : composite.finalGrade;

        if (finalGrade) finalGrades.push(finalGrade);
        countTerms(periodValues);
        expectedCount++;

        const out = [
          this.renderRow({
            key,
            label: subjectCategory.text,
            periodValues,
            finalGrade,
          }),
        ];

        parts.forEach((part, j) => {
          const grade = this.gradeFor(part.sectionSubject);
          out.push(this.renderRow({
            key: `${key}-${j}`,
            label: part.component.text,
            indent: true,
            periodValues: periods.map((p) => (grade ? Number(grade[p.dbField]) || 0 : 0)),
            finalGrade: subjectFinalGrade(grade, periods),
          }));
        });

        return out;
      }

      const sectionSubject = _.find(
        sectionSubjects,
        (o) => categoryMatches(value, o.subject.subjectCategory)
      );

      // A paired sub-row (Grade 6, JHS) has no Subject record of its own, so it
      // is the average of the two components actually taught - the same
      // synthesis MAPEH itself uses one level up.
      if (!sectionSubject && subjectCategory.members) {
        const memberGrades = subjectCategory.members
          .map((member) => this.gradeFor(
            _.find(sectionSubjects, (o) => categoryMatches(member, o.subject.subjectCategory))))
          .filter(Boolean);
        const pairComposite = mapehComposite(memberGrades, periods);

        return this.renderRow({
          key,
          label: subjectCategory.text,
          indent: true,
          periodValues: pairComposite.periodGrades,
          finalGrade: pairComposite.finalGrade,
        });
      }

      const grade = this.gradeFor(sectionSubject);
      const finalGrade = subjectFinalGrade(grade, periods);

      // Components sit under the MAPEH row and are not separate subjects, so
      // they never contribute to the average.
      if (!isMapehComponent(value)) {
        expectedCount++;
        countTerms(periods.map((p) => (grade ? Number(grade[p.dbField]) || 0 : 0)));
        // subjectFinalGrade returns 0 until the last term is graded, so pushing
        // it would make a Term 1 card average zero and print "Promoted". Leaving
        // it out keeps finalGrades short of expectedCount, which reads as INC.
        if (grade && finalGrade) finalGrades.push(finalGrade);
      }

      return this.renderRow({
        key,
        label: subjectCategory.text,
        indent: isMapehComponent(value),
        periodValues: periods.map((p) => (grade ? Number(grade[p.dbField]) || 0 : 0)),
        finalGrade,
      });
    });

    const classConduct =
      sectionStudent.classConducts && sectionStudent.classConducts.length !== 0
        ? sectionStudent.classConducts[0]
        : null;

    const conductFinal = conductFinalGrade(classConduct, periods);

    // A club is worth 5% of the general average. A learner in none scores
    // nothing for it - the registrar's decision, made knowing the cost.
    const clubs = clubRows(this.props.clubStudents, this.props.schoolYear);
    const clubMembership = clubs.length ? clubs[0].membership : null;
    const clubFinal = subjectFinalGrade(clubMembership, periods);

    const average = generalAverage(finalGrades, conductFinal, expectedCount, clubFinal);
    const adviser = _.find(teachers, (t) => t.teacherId === section.teacherId);

    return (
      <CardShell {...this.props} adviser={adviser}>
        <Grid.Row>
          <Grid.Column width={section.levelType !== "JHS" ? 11 : 16}>
            <Table celled size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Subject
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    colSpan={String(periods.length + 1)}
                    textAlign="center"
                  >
                    Grades
                  </Table.HeaderCell>
                  {/* The registrar's SY 2026-2027 form carries one Remarks
                      column. The Advancing/Benchmarking wording lives in the
                      performance-descriptor legend below the table, not beside
                      every row. */}
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Remarks
                  </Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                  {periods.map((p, i) => (
                    <Table.HeaderCell
                      key={p.index}
                      collapsing
                      textAlign="center"
                      style={
                        i === 0
                          ? { borderLeft: "1px solid rgba(34,36,38,.1)" }
                          : undefined
                      }
                    >
                      {p.shortLabel}
                    </Table.HeaderCell>
                  ))}
                  <Table.HeaderCell collapsing textAlign="center">
                    Final
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {rows}


                <Table.Row className="students-table-row">
                  <Table.Cell>
                    <span>Conduct</span>
                  </Table.Cell>
                  {periods.map((p) => {
                    const v = classConduct ? classConduct[p.conductField] : "";
                    return (
                      <Table.Cell key={p.index} textAlign="center">
                        {!v ? "" : v}
                      </Table.Cell>
                    );
                  })}
                  <Table.Cell textAlign="center">{conductFinal}</Table.Cell>
                  <Table.Cell />
                </Table.Row>

                {/* Clubs print under Conduct and stay out of the average. */}
                {clubRows(this.props.clubStudents, this.props.schoolYear).map((club, i) => {
                  const clubFinal = subjectFinalGrade(club.membership, periods);
                  return (
                    <Table.Row key={`club-${i}`} className="students-table-row">
                      <Table.Cell>
                        <span>{club.label}</span>
                      </Table.Cell>
                      {periods.map((p) => (
                        <Table.Cell key={p.index} textAlign="center">
                          {clubMark(club.membership, scheme, p)}
                        </Table.Cell>
                      ))}
                      <Table.Cell textAlign="center">
                        {!clubFinal ? "" : clubFinal}
                      </Table.Cell>
                      <Table.Cell />
                    </Table.Row>
                  );
                })}
                {/* Grade 1 prints no general average: DO 015 confers no Key
                    Stage 1 honors, and it had none when its marks were
                    letters. Gaining numbers should not have given it one. */}
                {scheme.usesGeneralAverage && (
                <Table.Row className="students-table-row">
                  <Table.Cell>
                    <span>General Average</span>
                  </Table.Cell>
                  {periods.map((p, i) => (
                    <Table.Cell key={p.index} textAlign="center">
                      <strong>
                        {this.state.showAverage
                          ? termAverage(
                              periodMarks[i],
                              expectedCount,
                              classConduct ? classConduct[p.conductField] : 0,
                              clubMembership ? clubMembership[p.dbField] : 0
                            )
                          : ""}
                      </strong>
                    </Table.Cell>
                  ))}
                  <Table.Cell textAlign="center">
                    <strong>
                      {/* The eye used to be the average's alternative, so
                          revealing it replaced the only control that could
                          hide it again - one way, until the page reloaded. It
                          stays beside the figure now. */}
                      {this.state.showAverage && average}
                      {this.canSeeAverage() && (
                        <Icon
                          name={this.state.showAverage ? "eye slash" : "eye"}
                          size="large"
                          title={
                            this.state.showAverage
                              ? "Hide the general average"
                              : "Show the general average"
                          }
                          style={{ cursor: "pointer", marginLeft: this.state.showAverage ? "0.5em" : 0 }}
                          onClick={this.toggleAverage}
                        />
                      )}
                    </strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {this.state.showAverage && average !== "INC" ? "Promoted" : ""}
                  </Table.Cell>
                </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Grid.Column>

          {section.levelType !== "JHS" && (
            <Grid.Column width={5}>
              <Table celled size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell textAlign="center">Term</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Remarks</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {periods.map((p, i) => {
                    /*
                     * What the term's general average means, in the school's
                     * own words, above whatever the adviser writes. It is the
                     * description alone - the band's name is already readable
                     * from the average itself - and it appears only once that
                     * term is fully marked, because a description of half a
                     * term's work describes nothing.
                     */
                    const band = scheme.bandFor(
                      termAverage(
                        periodMarks[i],
                        expectedCount,
                        classConduct ? classConduct[p.conductField] : 0,
                        clubMembership ? clubMembership[p.dbField] : 0
                      )
                    );
                    const written =
                      remarks && remarks.length !== 0 ? remarks[0][p.remarkField] : "";

                    return (
                      <Table.Row key={p.index} className="students-table-row">
                        <Table.Cell collapsing>{p.shortLabel}</Table.Cell>
                        <Table.Cell>
                          {band && <p>{band.description}</p>}
                          {written}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Grid.Column>
          )}
        </Grid.Row>
      </CardShell>
    );
  }

  /**
   * A mark as the card prints it.
   *
   * Grades 1 to 3 carry the letter beside the number - "90 (A)" - because Key
   * Stage 1 reports both to parents. The letter is read off the number by the
   * scheme rather than stored anywhere, so the two can never disagree. Every
   * other level prints the number alone, unchanged.
   */
  mark(value) {
    const { scheme } = this.props;
    if (!value) return "";
    if (!scheme.showLetterColumn) return value;

    const letter = scheme.letterFor(value);
    // A normal space lets the term column break "94" onto one line and "(A)"
    // onto the next, which is how it first printed. They are one mark.
    return letter ? `${value}\u00A0(${letter})` : value;
  }

  renderRow({ key, label, indent, periodValues, finalGrade }) {
    const { scheme } = this.props;

    return (
      <Table.Row key={key} className="students-table-row">
        <Table.Cell>
          <span style={indent ? MAPEH_INDENT : undefined}>{label}</span>
        </Table.Cell>
        {periodValues.map((v, i) => (
          <Table.Cell key={i} textAlign="center" style={NO_WRAP}>
            {this.mark(v)}
          </Table.Cell>
        ))}
        <Table.Cell textAlign="center" style={NO_WRAP}>
          {this.mark(finalGrade)}
        </Table.Cell>
        <Table.Cell textAlign="center">
          {indent ? "" : passFailLabel(finalGrade, scheme.passingGrade)}
        </Table.Cell>
      </Table.Row>
    );
  }
}

export default CardNumeric3Term;
